import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  SimpleGrid,
  Box,
  Text,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiMail,
  FiMessageCircle,
  FiClock,
  FiGitBranch,
  FiFileText,
  FiArrowRight,
  FiCheckSquare,
  FiEdit3,
  FiTag,
  FiSend,
  FiBell,
  FiUserPlus,
  FiUserX,
  FiGitMerge,
  FiShuffle,
  FiTarget,
  FiCalendar,
  FiPrinter,
  FiGrid,
} from 'react-icons/fi';
import useCalScheduling from '../../../../hooks/useCalScheduling';
import usePostcardAccess from '../../../../hooks/usePostcardAccess';

const NODE_TYPES = [
  // Communication
  { subType: 'SEND_EMAIL', label: 'Send Email', icon: FiMail, color: 'green', category: 'Communication' },
  { subType: 'SEND_SMS', label: 'Send SMS', icon: FiMessageCircle, color: 'green', category: 'Communication' },
  { subType: 'SEND_NOTIFICATION', label: 'Internal Alert', icon: FiBell, color: 'green', category: 'Communication' },
  // PostcardMania is admin-gated — only shown to admins / postcardEnabled users
  { subType: 'SEND_POSTCARD', label: 'Send Postcard', icon: FiPrinter, color: 'green', category: 'Communication', requiresPostcardAccess: true },

  // CRM Actions
  { subType: 'ADD_NOTE', label: 'Add Note', icon: FiFileText, color: 'teal', category: 'CRM Actions' },
  { subType: 'UPDATE_STAGE', label: 'Update Stage', icon: FiArrowRight, color: 'teal', category: 'CRM Actions' },
  { subType: 'CREATE_TASK', label: 'Create Task', icon: FiCheckSquare, color: 'teal', category: 'CRM Actions' },
  { subType: 'UPDATE_DEAL_FIELD', label: 'Update Deal Field', icon: FiEdit3, color: 'teal', category: 'CRM Actions' },
  { subType: 'MANAGE_TAG', label: 'Add / Remove Tag', icon: FiTag, color: 'teal', category: 'CRM Actions' },

  // Logic
  { subType: 'WAIT', label: 'Wait / Delay', icon: FiClock, color: 'orange', category: 'Logic' },
  { subType: 'WAIT_FOR', label: 'Wait For Condition', icon: FiTarget, color: 'orange', category: 'Logic' },
  // Cal.diy scheduling pilot — only shown when the per-user flag is on
  { subType: 'WAIT_UNTIL_MEETING', label: 'Wait Until Meeting', icon: FiCalendar, color: 'orange', category: 'Logic', requiresCalScheduling: true },
  { subType: 'IF_ELSE', label: 'If / Else', icon: FiGitBranch, color: 'purple', category: 'Logic' },
  { subType: 'SWITCH', label: 'Switch (Multi-Way)', icon: FiGitMerge, color: 'purple', category: 'Logic' },
  { subType: 'AB_SPLIT', label: 'A/B Split Test', icon: FiShuffle, color: 'purple', category: 'Logic' },

  // Integration
  { subType: 'SEND_WEBHOOK', label: 'Send Webhook', icon: FiSend, color: 'yellow', category: 'Integration' },
  { subType: 'APPEND_SHEET_ROW', label: 'Append to Google Sheet', icon: FiGrid, color: 'green', category: 'Integration' },
  { subType: 'ENROLL_CAMPAIGN', label: 'Enroll in Campaign', icon: FiUserPlus, color: 'blue', category: 'Integration' },
  { subType: 'UNENROLL_CAMPAIGN', label: 'Remove from Campaign', icon: FiUserX, color: 'red', category: 'Integration' },
];

const CATEGORIES = ['Communication', 'CRM Actions', 'Logic', 'Integration'];

export default function NodeTypeSelector({ isOpen, onClose, onSelect }) {
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const { enabled: calSchedulingEnabled } = useCalScheduling();
  const { canSendPostcards } = usePostcardAccess();
  const visibleNodeTypes = NODE_TYPES.filter(
    (n) =>
      (!n.requiresCalScheduling || calSchedulingEnabled) &&
      (!n.requiresPostcardAccess || canSendPostcards)
  );

  const renderCard = (item) => (
    <Box
      key={item.subType}
      bg={cardBg}
      borderRadius="lg"
      p={4}
      cursor="pointer"
      _hover={{ shadow: 'md', transform: 'translateY(-1px)' }}
      transition="all 0.15s"
      onClick={() => onSelect(item.subType)}
      textAlign="center"
    >
      <Icon as={item.icon} boxSize={6} color={`${item.color}.500`} mb={2} />
      <Text fontSize="sm" fontWeight="medium">
        {item.label}
      </Text>
    </Box>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add a Step</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {CATEGORIES.map((cat) => {
            const items = visibleNodeTypes.filter((n) => n.category === cat);
            if (!items.length) return null;
            return (
              <Box key={cat} mb={5}>
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500" mb={2}>
                  {cat}
                </Text>
                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                  {items.map(renderCard)}
                </SimpleGrid>
              </Box>
            );
          })}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
